import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';

export interface ListingRecord {
  id?: string;
  owner_id?: string;
  type: string;
  title: string;
  description: string;
  price: number | null;
  category_id: string;
  city: string;
  images: string[];
  files: string[];
  contact_name: string;
  contact_phone: string;
}

const listingTypes = [
  { value: 'annonce', label: 'Annonce / Offre' },
  { value: 'produit', label: 'Produit à vendre' },
];

const cities = ['Lomé', 'Sokodé', 'Kara', 'Atakpamé', 'Kpalimé', 'Tsévié', 'Dapaong', 'Autre'];

interface ListingFormProps {
  ownerId: string;
  existing?: ListingRecord | null;
  onSaved: () => void;
}

function emptyForm(): ListingRecord {
  return {
    type: 'annonce',
    title: '',
    description: '',
    price: null,
    category_id: '',
    city: 'Lomé',
    images: [],
    files: [],
    contact_name: '',
    contact_phone: '',
  };
}

function ListingForm({ ownerId, existing, onSaved }: ListingFormProps) {
  const [form, setForm] = useState<ListingRecord>(() => {
    if (existing) return { ...existing };
    return emptyForm();
  });
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data } = await supabase
        .from('categories')
        .select('id, name')
        .is('parent_id', null)
        .order('name');
      if (mounted && data) {
        setCategories(
          (data as { id: string; name: string }[]).map((c) => ({ value: c.id, label: c.name })),
        );
      }
      if (mounted) setCategoriesLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const update = (field: keyof ListingRecord, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    setErrorMsg('');
    try {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const url = await uploadFile(files[i], `listings/${ownerId}/images`);
        urls.push(url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Échec de l'upload des images.");
      setStatus('error');
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingFiles(true);
    setErrorMsg('');
    try {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const url = await uploadFile(files[i], `listings/${ownerId}/files`);
        urls.push(url);
      }
      setForm((prev) => ({ ...prev, files: [...prev.files, ...urls] }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Échec de l'upload des fichiers.");
      setStatus('error');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const removeFile = (index: number) => {
    setForm((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setStatus('error');
      setErrorMsg('Le titre est obligatoire.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      type: form.type,
      title: form.title.trim(),
      description: form.description.trim() || null,
      price: form.price !== null && form.price > 0 ? form.price : null,
      category_id: form.category_id || null,
      city: form.city.trim() || null,
      images: form.images,
      files: form.files,
      contact_name: form.contact_name.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('listings').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('listings').insert({
        ...payload,
        owner_id: ownerId,
      });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations principales */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-information-line text-primary-400" />
          </span>
          Informations de l&apos;annonce
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Type</label>
            <select
              value={form.type}
              onChange={(e) => update('type', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {listingTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Catégorie</label>
            <select
              value={form.category_id}
              onChange={(e) => update('category_id', e.target.value)}
              disabled={categoriesLoading}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400 disabled:opacity-50"
            >
              <option value="">Sélectionnez une catégorie</option>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Titre *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Ex : Vente de téléphone Samsung, Service de couture, Offre d'emploi..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Prix (FCFA)</label>
            <input
              type="number"
              value={form.price ?? ''}
              onChange={(e) => update('price', e.target.value ? Number(e.target.value) : null)}
              placeholder="25000 (laissez vide si gratuit)"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Ville</label>
            <select
              value={form.city}
              onChange={(e) => update('city', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Décrivez votre annonce : détails, état, conditions..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
          />
        </div>
      </section>

      {/* Photos */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
            <i className="ri-image-line text-accent-400" />
          </span>
          Photos
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {form.images.map((img, idx) => (
            <div key={idx} className="relative rounded-lg overflow-hidden bg-background-50 border border-background-200/70">
              <div className="h-32 w-full overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50"
                aria-label="Retirer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImages}
            className="h-32 rounded-lg border-2 border-dashed border-background-300/60 flex flex-col items-center justify-center gap-2 text-foreground-400 hover:border-primary-400/40 transition-colors disabled:opacity-50"
          >
            <i className="ri-add-line text-xl" />
            <span className="text-xs">{uploadingImages ? 'Envoi...' : 'Ajouter des photos'}</span>
          </button>
        </div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleImageUpload(e.target.files)}
        />
      </section>

      {/* Fichiers joints */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-file-3-line text-secondary-400" />
          </span>
          Fichiers joints
        </h2>
        <p className="text-sm text-foreground-400">
          Ajoutez des documents (PDF, documents, etc.) liés à votre annonce.
        </p>

        {form.files.length > 0 && (
          <div className="space-y-2">
            {form.files.map((file, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-500/10 shrink-0">
                  <i className="ri-file-line text-secondary-400" />
                </span>
                <a
                  href={file}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 text-sm text-foreground-50 truncate hover:text-primary-400 transition-colors"
                >
                  Fichier {idx + 1}
                </a>
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  aria-label="Retirer"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingFiles}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          <i className="ri-upload-2-line" />
          {uploadingFiles ? 'Envoi...' : 'Joindre un fichier'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFileUpload(e.target.files)}
        />
      </section>

      {/* Contact */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-contacts-line text-primary-400" />
          </span>
          Contact
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Nom du contact</label>
            <input
              type="text"
              value={form.contact_name}
              onChange={(e) => update('contact_name', e.target.value)}
              placeholder="Votre nom"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Téléphone</label>
            <input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => update('contact_phone', e.target.value)}
              placeholder="+228 90 00 00 00"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
        </div>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || uploadingImages || uploadingFiles}
          className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Publier l\'annonce'}
        </button>
      </div>
    </form>
  );
}

export default ListingForm;